import * as React from 'react'

import { useAuth } from '@/contexts/auth-context'
import { useChat } from '@/contexts/chat-context'

import {
  ChatHeader,
  ChatInput,
  ChatMessages,
  ChatMessagesEmptyState,
  ChatPopupContainer,
  ChatSidebar,
  CreateGroupChatDialog,
  DeleteConversationDialog,
  DeleteMessageDialog,
  EditConversationDialog,
  MobileChatInput,
  MobileConversationList,
  MobileMessagesScreen,
  UnifiedSearchDialog,
  UserSearchDialog,
  ViewPinnedMessagesDialog,
} from './components'
import { PinnedMessagesHeader } from './components/pinned-messages-header'
import {
  useChatActions,
  useChatScroll,
  useChatScrollBehavior,
  useChatState,
  useFileHandling,
  useMobileDetection,
} from './hooks'

import { ConversationType } from '@/types/chat.type'
import type { UserProfile } from '@/types/user.type'

interface GeneralMultiChatPopupProps {
  initialConversationId?: number
  targetUserId?: number
}

const ONLINE_WINDOW_MS = 5 * 60 * 1000

interface GeneralMultiChatPopupRef {
  openChat: (
    targetUserId?: number,
    targetUserName?: string,
    conversationId?: number,
    jobName?: string,
    applicationName?: string
  ) => void
  totalUnreadCount: number
  isConnected: boolean
}

const GeneralMultiChatPopup = React.forwardRef<GeneralMultiChatPopupRef, GeneralMultiChatPopupProps>(
  ({ initialConversationId, targetUserId: _targetUserId }, ref) => {
    const { user: authUser } = useAuth()
    const chatContext = useChat()

    // Custom hooks for state management
    const chatState = useChatState({ initialConversationId, authUser: authUser || null })
    const scrollManager = useChatScroll()
    const chatActions = useChatActions({
      chatState,
      chatContext,
      authUser: authUser || null,
      scrollManager,
    })
    const fileHandling = useFileHandling({ chatState, chatActions })

    // Scroll behavior hook for auto-scrolling
    useChatScrollBehavior({ scrollManager, isOpen: chatState.isOpen, authUser: authUser || null })

    // Compact layout: mobile OR minimized desktop popup
    const isCompactLayout = chatState.isMobileView || !chatState.isMaximized

    // Mobile detection hook
    const { goBackToConversations } = useMobileDetection({
      isMobileView: chatState.isMobileView,
      setIsMobileView: chatState.setIsMobileView,
      showMobileConversationList: chatState.showMobileConversationList,
      setShowMobileConversationList: chatState.setShowMobileConversationList,
      setSelectedConversationId: chatState.setSelectedConversationId,
      setTargetUserName: chatState.setTargetUserName,
      resetChatState: chatActions.resetChatState,
      markConversationAsRead: chatContext.markConversationAsRead,
      isCompactLayout,
    })

    // Wrapper for conversation selection that handles compact view
    const handleSelectConversation = React.useCallback(
      (conversationId: number) => {
        chatActions.handleSelectConversation(conversationId)
        // Always drop the list flag so a later minimize shows messages, not the list
        chatState.setShowMobileConversationList(false)
      },
      [chatActions, chatState]
    )

    // Handle create group chat
    const handleCreateGroupChat = React.useCallback(
      async (
        name: string,
        description: string,
        participantIds: number[],
        type?: ConversationType,
        addAllUsers?: boolean,
        courseIds?: number[]
      ) => {
        try {
          const newConversation = await chatContext.createGroupChat(
            name,
            description,
            participantIds,
            type,
            addAllUsers,
            courseIds
          )

          // Auto-select the newly created conversation
          if (newConversation) {
            chatState.setSelectedConversationId(newConversation.id)
          }

          chatState.setShowCreateGroupChatDialog(false)
        } catch (error) {
          // Error handling is done in the context
          // eslint-disable-next-line no-console
          console.error('Failed to create group chat:', error)
        }
      },
      [chatContext, chatState]
    )

    // Use refs to store the latest functions to avoid stale closures
    const loadConversationsRef = React.useRef(chatContext.debouncedLoadConversations)
    const resetChatStateRef = React.useRef(chatActions.resetChatState)
    const handlePendingTargetUserRef = React.useRef(chatActions.handlePendingTargetUser)
    const handleUnifiedSearchRef = React.useRef(chatActions.handleUnifiedSearch)

    // Update refs when functions change
    React.useEffect(() => {
      loadConversationsRef.current = chatContext.debouncedLoadConversations
      resetChatStateRef.current = chatActions.resetChatState
      handlePendingTargetUserRef.current = chatActions.handlePendingTargetUser
      handleUnifiedSearchRef.current = chatActions.handleUnifiedSearch
    }, [
      chatContext.debouncedLoadConversations,
      chatActions.resetChatState,
      chatActions.handlePendingTargetUser,
      chatActions.handleUnifiedSearch,
    ])

    // Pin/Unpin message handlers
    const handlePinMessage = React.useCallback(
      async (messageId: number) => {
        if (!chatState.selectedConversationId) return
        await chatContext.pinMessage(messageId, chatState.selectedConversationId)
        const refreshed = await chatContext.getPinnedMessages(chatState.selectedConversationId)
        chatState.setPinnedMessages(refreshed)
      },
      [chatState, chatContext]
    )

    const handleUnpinMessage = React.useCallback(
      async (messageId: number) => {
        if (!chatState.selectedConversationId) return
        await chatContext.unpinMessage(messageId, chatState.selectedConversationId)
        const refreshed = await chatContext.getPinnedMessages(chatState.selectedConversationId)
        chatState.setPinnedMessages(refreshed)
      },
      [chatState, chatContext]
    )

    // Memoize values to prevent unnecessary re-renders
    const memoizedAuthUser = React.useMemo((): UserProfile | null => {
      return authUser || null
    }, [authUser])

    const memoizedChatTitle = React.useMemo(() => {
      return chatActions.getChatTitle()
    }, [chatActions])

    const hasOnlineParticipantInActiveConversation = React.useMemo(() => {
      const participants = chatContext.conversation?.participants
      if (!participants || participants.length === 0) {
        return false
      }

      return participants.some(participant => {
        // Backend may return null user (e.g. deleted account)
        if (!participant.user || participant.user.id === authUser?.id) {
          return false
        }

        const lastActiveAt = participant.user.lastActiveAt
        if (!lastActiveAt) {
          return false
        }

        const lastActiveAtMs = new Date(lastActiveAt).getTime()
        if (Number.isNaN(lastActiveAtMs)) {
          return false
        }

        return Date.now() - lastActiveAtMs <= ONLINE_WINDOW_MS
      })
    }, [chatContext.conversation?.participants, authUser?.id])

    const memoizedFilteredConversations = React.useMemo(() => {
      return chatActions.getFilteredConversations()
    }, [chatActions])

    const memoizedFilteredMessages = React.useMemo(() => {
      return chatActions.getFilteredMessages()
    }, [chatActions])

    const mentionableUsers = React.useMemo(() => {
      if (!chatContext.conversation?.participants || !authUser?.id) {
        return []
      }

      return chatContext.conversation.participants
        .filter(
          participant =>
            participant.user != null &&
            participant.user.id !== authUser.id &&
            participant.status === 'active'
        )
        .map(participant => ({
          id: participant.user.id,
          firstName: participant.user.firstName,
          lastName: participant.user.lastName,
          avatar: participant.user.avatar,
          profileId: participant.user.profileId,
        }))
    }, [chatContext.conversation?.participants, authUser?.id])

    // Memoize the component to prevent unnecessary re-renders
    const memoizedComponent = React.useMemo(() => {
      if (!chatState.isOpen) {
        return null
      }

      return (
        <ChatPopupContainer
          onClose={chatActions.closeChat}
          isMaximized={chatState.isMobileView || chatState.isMaximized}
          isMobileFullscreen={chatState.isMobileView}
        >
          <ChatHeader
            title={
              isCompactLayout && chatState.showMobileConversationList ? 'Messages' : memoizedChatTitle
            }
            conversationName={
              isCompactLayout && chatState.showMobileConversationList ? 'Messages' : memoizedChatTitle
            }
            conversation={
              isCompactLayout && chatState.showMobileConversationList
                ? undefined
                : chatContext.conversation || undefined
            }
            hasOnlineParticipant={
              isCompactLayout && chatState.showMobileConversationList
                ? false
                : hasOnlineParticipantInActiveConversation
            }
            onSearch={() => chatState.setShowSearchDialog(true)}
            onCreateGroupChat={
              chatState.showCreateGroupChatDialog ? undefined : () => chatState.setShowCreateGroupChatDialog(true)
            }
            onClose={chatActions.closeChat}
            onBack={isCompactLayout && !chatState.showMobileConversationList ? goBackToConversations : undefined}
            onToggleMaximize={() => chatState.setIsMaximized(!chatState.isMaximized)}
            isMaximized={chatState.isMaximized}
            isPending={
              !!(chatState.pendingTargetUserId && (chatContext.isCreatingConversation || chatContext.isLoading))
            }
            isMobileConversationList={chatState.showMobileConversationList}
            compact={!chatState.isMobileView && !chatState.isMaximized}
          />

          {/* Remove the old ConversationSettingsOverlay section */}

          {/* Compact view: mobile OR minimized desktop popup */}
          {isCompactLayout ? (
            <div className="flex-1 flex flex-col min-h-0">
              {/* Mobile Conversation List */}
              {chatState.showMobileConversationList && (
                <MobileConversationList
                  conversations={memoizedFilteredConversations}
                  selectedConversationId={chatState.selectedConversationId}
                  unreadCounts={chatContext.unreadCounts}
                  isLoading={chatContext.isLoading}
                  onSelectConversation={handleSelectConversation}
                  onEditConversationName={chatActions.handleStartEditConversationName}
                  onDeleteConversation={chatActions.handleShowDeleteConversationConfirm}
                  onPinConversation={chatActions.handlePinConversation}
                  onUnpinConversation={chatActions.handleUnpinConversation}
                  getConversationTitle={chatActions.getConversationTitle}
                  getTargetedUser={chatActions.getTargetedUser}
                  formatTimestamp={chatActions.formatTimestamp}
                  targetUserAvatar={chatState.targetUserAvatar}
                  authUser={memoizedAuthUser}
                  conversationListRef={scrollManager.conversationListRef}
                />
              )}

              {/* Mobile Messages Screen */}
              {!chatState.showMobileConversationList && chatState.selectedConversationId && (
                <>
                  <PinnedMessagesHeader
                    pinnedMessages={chatState.pinnedMessages || []}
                    onViewPinnedMessages={() => chatState.setShowPinnedMessagesDialog(true)}
                  />
                  <MobileMessagesScreen
                    messages={memoizedFilteredMessages}
                    selectedConversationId={chatState.selectedConversationId}
                    isLoading={chatContext.isLoading}
                    isLoadingPrevious={chatContext.isLoadingPrevious}
                    isLoadingMore={chatContext.isLoadingMore}
                    hasPreviousMessages={chatContext.hasPreviousMessages}
                    hasMoreMessages={chatContext.hasMoreMessages}
                    contextMessagesEndIndex={chatContext.contextMessagesEndIndex}
                    searchQuery={chatState.searchQuery}
                    editingMessageId={chatState.editingMessageId}
                    editMessageContent={chatState.editMessageContent}
                    onLoadPreviousMessages={chatActions.handleLoadPreviousMessages}
                    onLoadMoreMessages={chatContext.loadMoreMessages}
                    onStartEditMessage={chatActions.handleStartEditMessage}
                    onEditMessage={chatActions.handleEditMessage}
                    onCancelEditMessage={chatActions.handleCancelEditMessage}
                    onEditMessageContentChange={chatState.setEditMessageContent}
                    onShowDeleteConfirm={chatActions.handleShowDeleteConfirm}
                    onStartReply={chatActions.handleStartReply}
                    onMessageSenderClick={chatActions.handleMessageSenderClick}
                    renderFileMessage={chatActions.renderFileMessage}
                    formatTimestamp={chatActions.formatTimestamp}
                    authUser={memoizedAuthUser}
                    scrollManager={scrollManager}
                    onScrollToMessage={chatContext.scrollToMessage}
                    onBackToConversations={goBackToConversations}
                    conversationTitle={memoizedChatTitle}
                    participants={chatContext.conversation?.participants || []}
                    onMessageReact={chatContext.reactToMessage}
                    highlightedMessageId={chatContext.highlightedMessageId}
                    onPinMessage={handlePinMessage}
                    onUnpinMessage={handleUnpinMessage}
                    isTyping={chatContext.isTyping}
                    typingUserName={chatContext.typingUserName}
                  />
                  <MobileChatInput
                    newMessage={chatState.newMessage}
                    selectedFile={chatState.selectedFile}
                    isUploadingFile={fileHandling.isUploadingFile}
                    pendingTargetUserId={chatState.pendingTargetUserId}
                    isCreatingConversation={chatContext.isCreatingConversation}
                    isLoading={chatContext.isLoading}
                    selectedConversationId={chatState.selectedConversationId}
                    targetUserName={chatState.targetUserName}
                    replyingToMessage={chatState.replyingToMessage}
                    onSendMessage={chatActions.handleSendMessage}
                    onReplyMessage={chatActions.handleReplyMessage}
                    onFileSelect={fileHandling.handleFileSelect}
                    onFileChange={fileHandling.handleFileChange}
                    onRemoveFile={fileHandling.handleRemoveFile}
                    onTyping={chatActions.handleTyping}
                    onInputFocus={chatActions.handleInputFocus}
                    onCancelReply={chatActions.handleCancelReply}
                    insertEmoji={chatActions.insertEmoji}
                    insertCustomEmoji={chatActions.insertCustomEmoji}
                    fileInputRef={fileHandling.fileInputRef}
                    inputRef={chatActions.inputRef}
                    mentionableUsers={mentionableUsers}
                  />
                </>
              )}

              {/* Mobile Empty State */}
              {!chatState.showMobileConversationList && !chatState.selectedConversationId && (
                <ChatMessagesEmptyState
                  pendingTargetUserId={chatState.pendingTargetUserId}
                  isCreatingConversation={chatContext.isCreatingConversation}
                  isLoading={chatContext.isLoading}
                />
              )}
            </div>
          ) : (
            /* Desktop Maximized View */
            <div className="flex flex-1 overflow-hidden">
              <ChatSidebar
                conversations={memoizedFilteredConversations}
                selectedConversationId={chatState.selectedConversationId}
                unreadCounts={chatContext.unreadCounts}
                isLoading={chatContext.isLoading}
                searchQuery={chatState.searchQuery}
                onSelectConversation={chatActions.handleSelectConversation}
                onEditConversationName={chatActions.handleStartEditConversationName}
                onDeleteConversation={chatActions.handleShowDeleteConversationConfirm}
                onPinConversation={chatActions.handlePinConversation}
                onUnpinConversation={chatActions.handleUnpinConversation}
                getConversationTitle={chatActions.getConversationTitle}
                getTargetedUser={chatActions.getTargetedUser}
                formatTimestamp={chatActions.formatTimestamp}
                authUser={memoizedAuthUser}
                targetUserAvatar={chatState.targetUserAvatar}
                conversationListRef={scrollManager.conversationListRef}
              />

              <div className="flex-1 flex flex-col">
                {!chatState.selectedConversationId ||
                (chatState.pendingTargetUserId && (chatContext.isCreatingConversation || chatContext.isLoading)) ? (
                  <ChatMessagesEmptyState
                    pendingTargetUserId={chatState.pendingTargetUserId}
                    isCreatingConversation={chatContext.isCreatingConversation}
                    isLoading={chatContext.isLoading}
                  />
                ) : (
                  <>
                    <PinnedMessagesHeader
                      pinnedMessages={chatState.pinnedMessages || []}
                      onViewPinnedMessages={() => chatState.setShowPinnedMessagesDialog(true)}
                    />
                    <ChatMessages
                      messages={memoizedFilteredMessages}
                      selectedConversationId={chatState.selectedConversationId}
                      isLoading={chatContext.isLoading}
                      isLoadingPrevious={chatContext.isLoadingPrevious}
                      isLoadingMore={chatContext.isLoadingMore}
                      hasPreviousMessages={chatContext.hasPreviousMessages}
                      hasMoreMessages={chatContext.hasMoreMessages}
                      contextMessagesEndIndex={chatContext.contextMessagesEndIndex}
                      searchQuery={chatState.searchQuery}
                      editingMessageId={chatState.editingMessageId}
                      editMessageContent={chatState.editMessageContent}
                      onLoadPreviousMessages={chatActions.handleLoadPreviousMessages}
                      onLoadMoreMessages={chatContext.loadMoreMessages}
                      onStartEditMessage={chatActions.handleStartEditMessage}
                      onEditMessage={chatActions.handleEditMessage}
                      onCancelEditMessage={chatActions.handleCancelEditMessage}
                      onEditMessageContentChange={chatState.setEditMessageContent}
                      onShowDeleteConfirm={chatActions.handleShowDeleteConfirm}
                      onStartReply={chatActions.handleStartReply}
                      onMessageSenderClick={chatActions.handleMessageSenderClick}
                      renderFileMessage={chatActions.renderFileMessage}
                      formatTimestamp={chatActions.formatTimestamp}
                      authUser={memoizedAuthUser}
                      scrollManager={scrollManager}
                      onScrollToMessage={chatContext.scrollToMessage}
                      participants={chatContext.conversation?.participants || []}
                      onMessageReact={chatContext.reactToMessage}
                      highlightedMessageId={chatContext.highlightedMessageId}
                      onPinMessage={handlePinMessage}
                      onUnpinMessage={handleUnpinMessage}
                      isTyping={chatContext.isTyping}
                      typingUserName={chatContext.typingUserName}
                    />
                  </>
                )}

                <ChatInput
                  newMessage={chatState.newMessage}
                  selectedFile={chatState.selectedFile}
                  isUploadingFile={fileHandling.isUploadingFile}
                  pendingTargetUserId={chatState.pendingTargetUserId}
                  isCreatingConversation={chatContext.isCreatingConversation}
                  isLoading={chatContext.isLoading}
                  selectedConversationId={chatState.selectedConversationId}
                  targetUserName={chatState.targetUserName}
                  replyingToMessage={chatState.replyingToMessage}
                  onMessageChange={chatState.setNewMessage}
                  onSendMessage={chatActions.handleSendMessage}
                  onFileSelect={fileHandling.handleFileSelect}
                  onFileChange={fileHandling.handleFileChange}
                  onRemoveFile={fileHandling.handleRemoveFile}
                  onTyping={chatActions.handleTyping}
                  onInputFocus={chatActions.handleInputFocus}
                  onKeyPress={chatActions.handleKeyPress}
                  onCancelReply={chatActions.handleCancelReply}
                  insertEmoji={chatActions.insertEmoji}
                  insertCustomEmoji={chatActions.insertCustomEmoji}
                  fileInputRef={fileHandling.fileInputRef}
                  inputRef={chatActions.inputRef}
                  mentionableUsers={mentionableUsers}
                />
              </div>
            </div>
          )}

          <EditConversationDialog
            isOpen={chatState.showEditConversationDialog}
            conversationName={chatState.editingConversationName}
            onClose={chatActions.handleCancelEditConversationName}
            onNameChange={chatState.setEditingConversationName}
            onSave={chatActions.handleSaveConversationName}
          />

          <UserSearchDialog
            isOpen={chatState.showUserSearch}
            onClose={() => chatState.setShowUserSearch(false)}
            onUserSelect={chatActions.handleUserSelect}
            authUser={memoizedAuthUser}
          />

          <CreateGroupChatDialog
            isOpen={chatState.showCreateGroupChatDialog}
            onClose={() => chatState.setShowCreateGroupChatDialog(false)}
            onCreateGroupChat={handleCreateGroupChat}
            authUser={memoizedAuthUser}
            isCreating={chatContext.isCreatingConversation}
          />

          <UnifiedSearchDialog
            isOpen={chatState.showSearchDialog}
            onClose={() => chatState.setShowSearchDialog(false)}
            searchQuery={chatState.searchQuery}
            onSearchQueryChange={chatState.setSearchQuery}
            searchResults={chatState.searchResults}
            onUserSelect={chatActions.handleUserProfileNavigation}
            onMessageSelect={chatActions.handleMessageSearchResult}
            onConversationSelect={chatActions.handleConversationSelectFromSearch}
            onSearch={chatActions.handleUnifiedSearch}
            formatTimestamp={chatActions.formatTimestamp}
          />

          <DeleteMessageDialog
            isOpen={chatState.showDeleteConfirmDialog}
            onClose={chatActions.handleCancelDelete}
            onConfirm={scope =>
              chatState.deletingMessageId && chatActions.handleDeleteMessage(chatState.deletingMessageId, scope)
            }
            isDeleting={chatState.isDeleting}
          />

          <DeleteConversationDialog
            isOpen={chatState.showDeleteConversationDialog}
            onClose={chatActions.handleCancelDeleteConversation}
            onConfirm={chatActions.handleDeleteConversation}
            isDeleting={chatState.isDeletingConversation}
            conversationType={
              chatState.deletingConversationId
                ? chatContext.conversations.find(conv => conv.id === chatState.deletingConversationId)?.type
                : undefined
            }
          />

          {chatState.showPinnedMessagesDialog && (
            <ViewPinnedMessagesDialog
              pinnedMessages={chatState.pinnedMessages || []}
              onClose={() => chatState.setShowPinnedMessagesDialog(false)}
              onScrollToMessage={messageId => {
                chatContext.scrollToMessage(messageId)
                chatState.setShowPinnedMessagesDialog(false)
              }}
              onUnpinMessage={handleUnpinMessage}
              formatTimestamp={chatActions.formatTimestamp}
            />
          )}
        </ChatPopupContainer>
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      chatState,
      chatContext.isLoading,
      chatContext.isCreatingConversation,
      chatContext.isLoadingPrevious,
      chatContext.hasMoreMessages,
      chatContext.unreadCounts,
      memoizedAuthUser,
      fileHandling.isUploadingFile,
      fileHandling.fileInputRef,
      chatActions,
      memoizedChatTitle,
      hasOnlineParticipantInActiveConversation,
      memoizedFilteredConversations,
      memoizedFilteredMessages,
      mentionableUsers,
      fileHandling.handleFileChange,
      fileHandling.handleFileSelect,
      fileHandling.handleRemoveFile,
      goBackToConversations,
      handleSelectConversation,
      handleCreateGroupChat,
      handlePinMessage,
      handleUnpinMessage,
      scrollManager,
    ])

    // Helper function to calculate total unread count
    const calculateTotalUnreadCount = React.useCallback((): number => {
      if (!chatContext.unreadCounts) return 0
      const counts = Object.values(chatContext.unreadCounts) as number[]
      return counts.reduce((sum: number, count: number) => sum + count, 0)
    }, [chatContext.unreadCounts])

    // Expose methods via ref
    React.useImperativeHandle(
      ref,
      () => ({
        openChat: (targetUserId?: number, targetUserName?: string, conversationId?: number, jobName?: string) => {
          chatActions.openChat(targetUserId, targetUserName, conversationId, jobName)
        },
        scrollToMessage: scrollManager.scrollToMessage,
        scrollToMessageWithRetries: scrollManager.scrollToMessageWithRetries,
        beginMessageNavigationScroll: scrollManager.beginMessageNavigationScroll,
        totalUnreadCount: calculateTotalUnreadCount(),
        isConnected: chatContext.isConnected,
      }),
      [chatContext.isConnected, calculateTotalUnreadCount, chatActions, scrollManager]
    )

    // Handle conversation selection when conversations are loaded
    React.useEffect(() => {
      if (
        chatState.isOpen &&
        chatState.selectedConversationId &&
        chatContext.conversations.length > 0 &&
        !chatContext.isLoading
      ) {
        const conversationExists = chatContext.conversations.some(conv => conv.id === chatState.selectedConversationId)
        if (!conversationExists) {
          chatState.setSelectedConversationId(undefined)
        } else {
          chatContext.setConversationId(chatState.selectedConversationId)
        }
      }
    }, [
      chatState.isOpen,
      chatState.selectedConversationId,
      chatContext.conversations,
      chatContext.isLoading,
      chatContext.setConversationId,
      chatContext,
      chatState,
    ])

    // Sync selectedConversationId with context's conversation when context auto-selects
    React.useEffect(() => {
      if (chatContext.conversation?.id && !chatState.selectedConversationId && chatState.isOpen) {
        chatState.setSelectedConversationId(chatContext.conversation.id)
      }
    }, [chatContext.conversation?.id, chatState.selectedConversationId, chatState.isOpen, chatState])

    // Auto-select an initial conversation so first-time open shows an opponent/chat immediately.
    React.useEffect(() => {
      if (!chatState.isOpen || chatState.selectedConversationId || chatContext.isLoading) {
        return
      }

      if (chatState.pendingTargetUserId || chatContext.conversation) {
        return
      }

      const fallbackConversation = memoizedFilteredConversations.find(conv => conv?.id)
      if (!fallbackConversation?.id) {
        return
      }

      chatState.setSelectedConversationId(fallbackConversation.id)
      chatContext.setConversationId(fallbackConversation.id)
    }, [
      chatState.isOpen,
      chatState.selectedConversationId,
      chatState.pendingTargetUserId,
      chatContext.isLoading,
      chatContext.conversation,
      chatContext.setConversationId,
      memoizedFilteredConversations,
      chatContext,
      chatState,
    ])

    // Load conversations when chat opens.
    // Important: if API returns an empty array, treat it as loaded to avoid request loops.
    React.useEffect(() => {
      if (chatState.isOpen) {
        // Always ensure conversations are loaded when chat is open
        // This prevents conversations from disappearing when switching tabs
        if (chatContext.conversations.length === 0 && !chatContext.isLoading && !chatContext.hasLoadedConversations) {
          loadConversationsRef.current()
        }
      } else {
        resetChatStateRef.current()
      }
    }, [chatState.isOpen, chatContext.conversations.length, chatContext.isLoading, chatContext.hasLoadedConversations])

    // Sync chat open state with context
    React.useEffect(() => {
      chatContext.setIsChatOpen(chatState.isOpen)
    }, [chatState.isOpen, chatContext])

    // Load pinned messages whenever the selected conversation changes while chat is open
    React.useEffect(() => {
      const loadPinned = async () => {
        if (!chatState.isOpen || !chatState.selectedConversationId) return
        const pinned = await chatContext.getPinnedMessages(chatState.selectedConversationId)
        chatState.setPinnedMessages(pinned)
      }
      loadPinned()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatState.selectedConversationId, chatState.isOpen])

    // Handle target user ID when chat opens - FIXED: removed function dependencies
    React.useEffect(() => {
      if (
        chatState.isOpen &&
        chatState.pendingTargetUserId &&
        !chatContext.isLoading &&
        chatContext.hasLoadedConversations &&
        !chatState.hasCheckedExistingConversation
      ) {
        handlePendingTargetUserRef.current()
      }
    }, [
      chatState.isOpen,
      chatState.pendingTargetUserId,
      chatContext.isLoading,
      chatContext.hasLoadedConversations,
      chatState.hasCheckedExistingConversation,
    ]) // Removed chatActions dependency

    // Update filtered conversations when conversations change.
    // Keep valid direct conversations even if they do not have messages yet.
    React.useEffect(() => {
      const visibleConversations = chatContext.conversations.filter(conv => {
        // Check if conversation has any messages (from metadata or currently loaded messages)
        const hasMessagesFromConv = conv.lastMessageContent || conv.lastMessageAt
        const hasMessagesFromCurrent = chatContext.messages.length > 0 && chatContext.conversation?.id === conv.id
        const hasMessages = hasMessagesFromConv || hasMessagesFromCurrent

        // Only include valid conversation objects
        const isValidConversation = conv.id && conv.participants && conv.participants.length > 0

        const isGroupConversation =
          conv.type === 'group' || conv.type === 'course' || conv.type === 'private_group' || conv.type === 'community'
        const isDirectConversation = conv.type === 'direct' || conv.participants.length === 2

        // Include the currently selected conversation even if it has no messages yet
        const isCurrentlySelected = conv.id === chatContext.conversation?.id

        return (
          (hasMessages || isGroupConversation || isDirectConversation || isCurrentlySelected) && isValidConversation
        )
      })

      chatState.setFilteredConversations(visibleConversations)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      chatContext.conversations,
      chatContext.messages,
      chatContext.conversation?.id,
      chatState.setFilteredConversations,
    ])

    // Update filtered messages when messages change
    React.useEffect(() => {
      chatState.setFilteredMessages(chatContext.messages)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatContext.messages, chatState.setFilteredMessages])

    // Trigger search when search dialog opens - FIXED: removed function dependencies
    React.useEffect(() => {
      if (chatState.showSearchDialog && chatState.searchQuery.trim()) {
        handleUnifiedSearchRef.current()
      }
    }, [chatState.showSearchDialog, chatState.searchQuery]) // Removed chatActions dependency

    // Check if isConversationRead is true and call mark conversation as read API
    React.useEffect(() => {
      if (chatState.isConversationRead && chatState.selectedConversationId && chatState.isOpen) {
        // Call the API to mark conversation as read
        chatContext
          .markConversationAsRead(chatState.selectedConversationId)
          .then(() => {
            // Set isConversationRead to false after successful API call
            chatState.setIsConversationRead(false)
          })
          .catch(error => {
            // Set isConversationRead to false even if API call fails
            chatState.setIsConversationRead(false)
            // eslint-disable-next-line no-console
            console.error('Failed to mark conversation as read:', error)
          })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatState.isConversationRead])

    return memoizedComponent
  }
)

GeneralMultiChatPopup.displayName = 'GeneralMultiChatPopup'

export default GeneralMultiChatPopup
